import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  SimpleGrid,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Heading,
} from '@chakra-ui/react';
import { BulletList } from 'react-content-loader';

const Personal = ({
  isEditingPersonal,
  eligible,
  setIsEditingPersonal,
  formData,
  handleChange,
  handleDateChange,
  formErrors,
  saveData,
  disable,
  isEditingContact,
  setIsEditingContact,
  validateEmail,
  inputErrors,
  validatePhoneNumber,
  disableContact,
  isSaving,
  saveContactData
}) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  /* ── shared input style ── */
  const inputStyle = (editing) => ({
    border: editing ? '1px solid #e2e8f0' : 'none',
    width: '100%',
    backgroundColor: editing ? '#f7fafc' : '#fff',
    padding: '8px 10px',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'background 0.2s',
  });

  const sectionBox = {
    border: '1px solid',
    borderColor: 'gray.200',
    bg: 'white',
    boxShadow: 'sm',
    borderRadius: 'lg',
    p: 5,
    mb: 4,
  };

  return (
    <React.Fragment>
      {loading ? (
        <BulletList />
      ) : (
        <>
          {/* ── Personal Info ── */}

          <Box {...sectionBox}>
            <Flex
              justify="space-between"
              align="center"
              mb={5}
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 3, md: 0 }}
            >
              <Flex align="center" gap={2}>
                <Box w="4px" h="20px" bg="purple.500" borderRadius="full" />
                <Heading size="md" mb={0}>Personal Info</Heading>
              </Flex>
              {!isEditingPersonal && eligible && (
                <Button
                  colorScheme="purple"
                  size="sm"
                  borderRadius="full"
                  px={5}
                  onClick={() => setIsEditingPersonal(true)}
                >
                  Edit
                </Button>
              )}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {/* Name */}
              <FormControl>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                  Name
                </FormLabel>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={!isEditingPersonal}
                  placeholder="Enter your name"
                  style={inputStyle(isEditingPersonal)}
                />
              </FormControl>

              {/* Blood Group */}
              <FormControl>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                  Blood Group
                </FormLabel>
                <input
                  type="text"
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleChange}
                  disabled={!isEditingPersonal}
                  placeholder="e.g. A+"
                  style={inputStyle(isEditingPersonal)}
                />
              </FormControl>

              {/* Gender */}
              <FormControl>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                  Gender
                </FormLabel>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  disabled={!isEditingPersonal}
                  style={inputStyle(isEditingPersonal)}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </FormControl>

              {/* Date of Birth */}
              <FormControl>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                  Date of Birth
                </FormLabel>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={(e) => { handleChange(e); handleDateChange(e); }}
                  disabled={!isEditingPersonal}
                  style={inputStyle(isEditingPersonal)}
                />
                {formErrors.dob && (
                  <Text color="red.500" fontSize="xs" mt={1}>{formErrors.dob}</Text>
                )}
              </FormControl>

              {/* Marital Status */}
              <FormControl>
                <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                  Marital Status
                </FormLabel>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                  disabled={!isEditingPersonal}
                  style={inputStyle(isEditingPersonal)}
                >
                  <option value="">Select Status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </select>
              </FormControl>
            </SimpleGrid>

            {isEditingPersonal && (
              <HStack spacing={3} justify="flex-end" mt={5}>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  borderRadius="full"
                  px={5}
                  onClick={() => setIsEditingPersonal(false)}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isSaving}
                  colorScheme="purple"
                  borderRadius="full"
                  px={6}
                  isDisabled={disable}
                  onClick={saveData}
                >
                  Save
                </Button>
              </HStack>
            )}
          </Box>

          {/* ── Contact Info ── */}
          <Box {...sectionBox} mt={2}>
            <Flex
              justify="space-between"
              align="center"
              mb={5}
              direction={{ base: 'column', md: 'row' }}
              gap={{ base: 3, md: 0 }}
            >
              <Flex align="center" gap={2}>
                <Box w="4px" h="20px" bg="purple.500" borderRadius="full" />
                <Heading size="md" mb={0}>Contact Info</Heading>
              </Flex>
              {!isEditingContact && eligible && (
                <Button
                  colorScheme="purple"
                  size="sm"
                  borderRadius="full"
                  px={5}
                  onClick={() => setIsEditingContact(true)}
                >
                  Edit
                </Button>
              )}
            </Flex>

            <VStack spacing={5} align="stretch">
              {/* Emails */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Official Email ID
                  </FormLabel>
                  <input
                    type="email"
                    name="official_email_id"
                    value={formData.official_email_id}
                    onChange={(e) => { validateEmail(e); handleChange(e); }}
                    disabled={!isEditingContact}
                    placeholder="official@company.com"
                    style={inputStyle(isEditingContact)}
                  />
                  {inputErrors.official_email_id && (
                    <Text color="red.500" fontSize="xs" mt={1}>{inputErrors.official_email_id}</Text>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Personal Email ID
                  </FormLabel>
                  <input
                    type="email"
                    name="personal_email_id"
                    value={formData.personal_email_id}
                    onChange={(e) => { validateEmail(e); handleChange(e); }}
                    disabled={!isEditingContact}
                    placeholder="personal@email.com"
                    style={inputStyle(isEditingContact)}
                  />
                  {inputErrors.personal_email_id && (
                    <Text color="red.500" fontSize="xs" mt={1}>{inputErrors.personal_email_id}</Text>
                  )}
                </FormControl>
              </SimpleGrid>

              {/* Phone numbers */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Phone Number
                  </FormLabel>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={(e) => { validatePhoneNumber(e); handleChange(e); }}
                    disabled={!isEditingContact}
                    placeholder="Enter phone number"
                    style={inputStyle(isEditingContact)}
                  />
                  {inputErrors.phone_number && (
                    <Text color="red.500" fontSize="xs" mt={1}>{inputErrors.phone_number}</Text>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Alternate Phone Number
                  </FormLabel>
                  <input
                    type="tel"
                    name="alternate_phone_number"
                    value={formData.alternate_phone_number}
                    onChange={(e) => { validatePhoneNumber(e); handleChange(e); }}
                    disabled={!isEditingContact}
                    placeholder="Enter alternate number"
                    style={inputStyle(isEditingContact)}
                  />
                  {inputErrors.alternate_phone_number && (
                    <Text color="red.500" fontSize="xs" mt={1}>{inputErrors.alternate_phone_number}</Text>
                  )}
                </FormControl>
              </SimpleGrid>

              {/* Addresses */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Current Address
                  </FormLabel>
                  <textarea
                    name="current_address"
                    value={formData.current_address}
                    onChange={handleChange}
                    disabled={!isEditingContact}
                    placeholder="Enter current address"
                    rows={3}
                    style={{ ...inputStyle(isEditingContact), resize: 'vertical' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="600" fontSize="sm" color="gray.500" mb={1}>
                    Permanent Address
                  </FormLabel>
                  <textarea
                    name="permanent_address"
                    value={formData.permanent_address}
                    onChange={handleChange}
                    disabled={!isEditingContact}
                    placeholder="Enter permanent address"
                    rows={3}
                    style={{ ...inputStyle(isEditingContact), resize: 'vertical' }}
                  />
                </FormControl>
              </SimpleGrid>

              {isEditingContact && (
                <HStack spacing={3} justify="flex-end" mt={2}>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    borderRadius="full"
                    px={5}
                    onClick={() => setIsEditingContact(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    isLoading={isSaving}
                    colorScheme="purple"
                    borderRadius="full"
                    px={6}
                    isDisabled={disableContact}
                    onClick={saveContactData}
                  >
                    Save
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>
        </>
      )}
    </React.Fragment>
  );
};

export default Personal;